import { useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';

import {
	Recipient,
	RecipientUserOption,
	isUserRecipientOption,
	RecipientOption,
	IEmailRecipient,
} from 'packages/kakapo-ui-services/models/Recipient';
import Select from 'packages/kakapo-ui-kit/components/forms/controls/Select';
import Label from 'packages/kakapo-ui-kit/components/forms/controls/Label';
import RecipientFieldReadonly from 'packages/kakapo-ui-kit/components/recipients/RecipientFieldReadonly';
import RecipientSelectorOption from 'packages/kakapo-ui-kit/components/recipients/RecipientSelectorOption';
import RecipientService from 'packages/kakapo-ui-services/services/recipients/RecipientsService';
import useImperativeSearch from 'packages/kakapo-ui-application/hooks/useImperativeSearch';
import { Button, Callout, Input, Separator, Subheading } from '@treams/treams-ui';
import { Box, Flex } from 'reflexbox';
import { validator } from 'packages/kakapo-ui-services/factories/ValidatorFactory';
import { Form, FormikProvider, useFormik } from 'formik';

interface IProps {
	initialRecipients?: Array<Recipient>;
	excludeRecipients?: Array<Recipient>;
	onChange: (recipients: Array<Recipient>) => void;
	footer: ReactNode;
}

const transformOptionToUserRecipient = (option: RecipientUserOption): Recipient => {
	const { data, value } = option;
	return {
		userId: value,
		userData: data!,
	};
};

export const RecipientSelectorControl = function RecipientSelectorControl(props: {
	recipients: Recipient[];
	onAddRecipient: (recipient: Recipient) => void;
	autoFocus?: boolean;
	isDisabled?: boolean;
}) {
	const { t } = useTranslation();

	const [options, setOptions] = useState<Array<RecipientOption>>([]);
	const [selectedOption, setSelectedOption] = useState<any>(null);
	const [inputValue, setInputValue] = useState<string>('');

	const [inputValueDebounced] = useDebounce(inputValue, 200);

	const search = useImperativeSearch();

	async function searchOptionsByQuery(query: string) {
		if (query.length === 0) {
			return [];
		}

		return (await search(query, { filters: 'isArchived:false' })).map((user) => ({
			label: `${user.firstName} ${user.lastName}`,
			value: user.id,
			data: {
				...user,
				userId: user.id,
			},
		})) as any;
	}

	const handleSelectChange = (option: any) => {
		setSelectedOption(null);

		if (isUserRecipientOption(option as RecipientOption)) {
			const recipient = transformOptionToUserRecipient(option);
			props.onAddRecipient(recipient);

			return;
		}
	};

	useEffect(() => {
		searchOptionsByQuery(inputValue).then(setOptions);
	}, [inputValueDebounced]);

	return (
		<Select
			unspaced
			options={RecipientService.filterPreviouslyAddedOptions(options, props.recipients)}
			placeholder={t('recipient.selector.colleague.search')}
			noOptionsMessage={() =>
				inputValue.length > 0
					? t('recipients.selector.noResults')
					: t('recipients.selector.startTypingToSearch')
			}
			onInputChange={setInputValue}
			inputValue={inputValue}
			onChange={handleSelectChange}
			value={selectedOption}
			components={
				{
					Option: RecipientSelectorOption,
				} as any
			}
			autoFocus={props.autoFocus}
			isDisabled={props.isDisabled}
		/>
	);
};

function ExternalRecipientSelector(props: {
	onAddRecipient: (recipient: { name: string; email: string }) => void;
}) {
	const { t } = useTranslation();

	const formik = useFormik({
		initialValues: {
			name: '',
			email: '',
		},
		validationSchema: validator.object({
			name: validator.string().required(),
			email: validator.string().email().required(),
		}),
		onSubmit: (values, { resetForm, setTouched }) => {
			props.onAddRecipient(values);
			(document.activeElement as any)?.blur();
			resetForm();
			setTouched({ name: false, email: false });
		},
	});

	return (
		<FormikProvider value={formik}>
			<Form>
				<Label title={t('recipient.selector.external.title')} />

				<Flex mb={2}>
					<Box mr={2} width={1 / 2}>
						<Label title={t('recipient.selector.external.fullName')} />
						<Input.Formik name="name" />
					</Box>
					<Box width={1 / 2}>
						<Label title={t('recipient.selector.external.email')} />
						<Input.Formik name="email" />
					</Box>
				</Flex>
				<Flex>
					<button type="submit" style={{ display: 'none' }} />
					<Button
						type="transparent"
						onClick={() => formik.submitForm()}
						title={t('recipient.selector.external.submit')}
						small
					/>
				</Flex>
			</Form>
		</FormikProvider>
	);
}

const RecipientSelector = ({ initialRecipients, excludeRecipients, onChange, footer }: IProps) => {
	const { t } = useTranslation();

	const [recipients, setRecipients] = useState<Array<Recipient>>(initialRecipients ?? []);
	const excluded = !!excludeRecipients ? recipients.concat(excludeRecipients) : recipients;

	useEffect(() => {
		onChange(recipients);
	}, [recipients]);

	const removeRecipientAtIndex = (index: number): void => {
		const recipientsCopy = [...recipients];
		recipientsCopy.splice(index, 1);
		setRecipients(recipientsCopy);
	};

	return (
		<Flex flexDirection={['column', 'row']}>
			<Box width={[1, 7 / 12]} mr={3}>
				<Subheading mb={1}>{t('recipient.selector.add.title')}</Subheading>
				<Box mb={3}>
					<Label title={t('recipient.selector.colleague.title')} />

					<RecipientSelectorControl
						// This will force a re-render on select after the length changes
						// this will clear the input field and make the placeholder visible again
						key={excluded.length}
						recipients={excluded}
						onAddRecipient={(recipient) => setRecipients([...recipients, recipient])}
					/>
				</Box>

				<Separator mb={3} color="slateBlue" backgroundColor="whiteBlue">
					{t('recipient.selector.or')}
				</Separator>

				<Box mb={[3, 4]}>
					<ExternalRecipientSelector
						onAddRecipient={(recipient) => setRecipients([...recipients, recipient])}
					/>
				</Box>
				<Box display={['none', 'block']}>{footer}</Box>
			</Box>
			<Box width={[1, 5 / 12]}>
				<Subheading mb={1}>{t('recipient.selector.list.title')}</Subheading>
				<Box mb={[4, 3]}>
					{recipients.length === 0 && (
						<Callout color={'red'}>{t('recipient.selector.list.empty')} </Callout>
					)}
					{recipients.map((recipient, index) => (
						<RecipientFieldReadonly
							key={index}
							recipient={recipient}
							onRemove={() => removeRecipientAtIndex(index)}
						/>
					))}
				</Box>
			</Box>
			<Box display={['block', 'none']}>{footer}</Box>
		</Flex>
	);
};

export default RecipientSelector;
